import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Globe, Mail, Phone, GraduationCap, Users } from "lucide-react";

export default function FlightSchools() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/flight-schools").then((r) => setList(r.data)); }, []);

  const schools = useMemo(() => list.filter((s) => (s.type || "flight_school") === "flight_school"), [list]);
  const orgs = useMemo(() => list.filter((s) => s.type === "organization"), [list]);

  return (
    <div data-testid="flight-schools-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Based at KSME</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-950 mt-3">Organizations & flight training.</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Flight schools, civic organizations, and aviation partners that call Lake Cumberland Regional home.
        Contact each directly for hours, rates, and programs.
      </p>

      <Section
        title="Flight Training"
        icon={GraduationCap}
        items={schools}
        empty="No flight schools listed yet."
        testid="org-flight"
      />

      <div className="my-16 flex items-center gap-6" aria-hidden="true">
        <div className="flex-1 h-px bg-slate-200" />
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-400">Other Organizations</div>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <Section
        title="Other Organizations"
        icon={Users}
        items={orgs}
        empty="No additional organizations listed yet."
        testid="org-other"
        hideHeader
      />
    </div>
  );
}

function Section({ title, icon: Icon, items, empty, testid, hideHeader }) {
  return (
    <section data-testid={testid}>
      {!hideHeader && (
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-sky-600 mb-6 mt-12">
          <Icon className="h-4 w-4" /> {title}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        {items.map((s) => (
          <div key={s.id} data-testid={`school-${s.id}`} className="bg-white border border-slate-200 p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="font-display text-2xl font-semibold text-slate-950 leading-tight">{s.name}</div>
              {s.logo_url && (
                <img
                  src={s.logo_url}
                  alt={`${s.name} logo`}
                  className="h-12 w-auto max-w-[140px] object-contain shrink-0"
                />
              )}
            </div>
            {s.description && <p className="mt-3 text-slate-600 leading-relaxed">{s.description}</p>}

            {Array.isArray(s.programs) && s.programs.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {s.programs.map((p) => (
                  <span key={p} className="text-[11px] font-mono uppercase tracking-widest border border-slate-300 px-2 py-1">
                    {p}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-slate-200 grid sm:grid-cols-2 gap-3 text-sm">
              {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-2 hover:text-sky-600"><Phone className="h-4 w-4" />{s.phone}</a>}
              {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:text-sky-600"><Mail className="h-4 w-4" />{s.email}</a>}
              {s.website && (
                <a target="_blank" rel="noreferrer" href={s.website} className="flex items-center gap-2 hover:text-sky-600 sm:col-span-2">
                  <Globe className="h-4 w-4" />{s.website}
                </a>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-2 text-slate-500 text-sm">{empty}</div>}
      </div>
    </section>
  );
}
